/**
 * Date Utilities for Istanbul Timezone
 *
 * Tüm tarih/saat işlemleri Istanbul timezone'unda (Europe/Istanbul, UTC+3) yapılmalıdır.
 * Bu utility fonksiyonları, proje genelinde tutarlı tarih/saat işlemleri sağlar.
 */

import { startOfDay, endOfDay, isSameDay } from "date-fns";
import { toZonedTime, fromZonedTime, format as formatTz } from "date-fns-tz";
import { tr } from "date-fns/locale";

export const ISTANBUL_TZ = "Europe/Istanbul";

/**
 * Istanbul timezone'unda şu anki tarih/saat
 * @returns {Date} Istanbul timezone'unda şu anki tarih/saat
 */
export function getIstanbulDate(): Date {
  return toZonedTime(new Date(), ISTANBUL_TZ);
}

/**
 * Istanbul timezone'unda bugünün başlangıcı (00:00:00)
 * @returns {Date} Istanbul timezone'unda bugünün başlangıcı
 */
export function getIstanbulTodayStart(): Date {
  const istanbulNow = getIstanbulDate();
  return startOfDay(istanbulNow);
}

/**
 * Istanbul timezone'unda bugünün sonu (23:59:59.999)
 * @returns {Date} Istanbul timezone'unda bugünün sonu
 */
export function getIstanbulTodayEnd(): Date {
  const istanbulNow = getIstanbulDate();
  return endOfDay(istanbulNow);
}

/**
 * Istanbul timezone'unda belirli bir tarihin başlangıcı (00:00:00)
 * @param {Date|string} date - Tarih
 * @returns {Date} Istanbul timezone'unda tarihin başlangıcı
 */
export function getIstanbulDateStart(date: Date | string): Date {
  const d = typeof date === "string" ? new Date(date) : date;
  const istanbulDate = toZonedTime(d, ISTANBUL_TZ);
  return startOfDay(istanbulDate);
}

/**
 * Istanbul timezone'unda belirli bir tarihin sonu (23:59:59.999)
 * @param {Date|string} date - Tarih
 * @returns {Date} Istanbul timezone'unda tarihin sonu
 */
export function getIstanbulDateEnd(date: Date | string): Date {
  const d = typeof date === "string" ? new Date(date) : date;
  const istanbulDate = toZonedTime(d, ISTANBUL_TZ);
  return endOfDay(istanbulDate);
}

/**
 * Istanbul timezone'unda tarih formatla
 * @param {Date|string|null|undefined} date - Tarih
 * @param {string} formatStr - Format string (date-fns format)
 * @returns {string|null} Formatlanmış tarih string
 */
export function formatIstanbulDate(
  date: Date | string | null | undefined,
  formatStr: string = "dd.MM.yyyy"
): string | null {
  if (!date) return null;
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return null;
  const istanbulDate = toZonedTime(d, ISTANBUL_TZ);
  return formatTz(istanbulDate, formatStr, {
    timeZone: ISTANBUL_TZ,
    locale: tr,
  });
}

/**
 * Istanbul timezone'unda tarih ve saat formatla
 * @param {Date|string|null|undefined} date - Tarih
 * @param {string} formatStr - Format string (date-fns format)
 * @returns {string|null} Formatlanmış tarih/saat string
 */
export function formatIstanbulDateTime(
  date: Date | string | null | undefined,
  formatStr: string = "dd.MM.yyyy HH:mm"
): string | null {
  if (!date) return null;
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return null;
  const istanbulDate = toZonedTime(d, ISTANBUL_TZ);
  return formatTz(istanbulDate, formatStr, {
    timeZone: ISTANBUL_TZ,
    locale: tr,
  });
}

/**
 * Istanbul timezone'unda sadece tarih (saat olmadan) oluştur
 * @param {Date|string|null|undefined} date - Tarih
 * @returns {Date} Istanbul timezone'unda sadece tarih (saat 00:00:00)
 */
export function getIstanbulDateOnly(date?: Date | string | null): Date {
  const d = date || getIstanbulDate();
  const dateObj = typeof d === "string" ? new Date(d) : d;
  const istanbulDate = toZonedTime(dateObj, ISTANBUL_TZ);
  const year = istanbulDate.getFullYear();
  const month = istanbulDate.getMonth();
  const day = istanbulDate.getDate();

  // Istanbul timezone'unda yeni tarih oluştur (saat 00:00:00)
  const dateOnly = new Date(year, month, day);
  // UTC'ye çevir (API için)
  return fromZonedTime(dateOnly, ISTANBUL_TZ);
}

/**
 * Istanbul timezone'unda iki tarihin aynı gün olup olmadığını kontrol et
 * @param {Date|string} date1 - İlk tarih
 * @param {Date|string} date2 - İkinci tarih
 * @returns {boolean} Aynı gün mü?
 */
export function isSameIstanbulDay(
  date1: Date | string,
  date2: Date | string
): boolean {
  const d1 = typeof date1 === "string" ? new Date(date1) : date1;
  const d2 = typeof date2 === "string" ? new Date(date2) : date2;
  const istanbulDate1 = toZonedTime(d1, ISTANBUL_TZ);
  const istanbulDate2 = toZonedTime(d2, ISTANBUL_TZ);
  return isSameDay(istanbulDate1, istanbulDate2);
}

/**
 * Istanbul timezone'unda bugün mü kontrol et
 * @param {Date|string} date - Tarih
 * @returns {boolean} Bugün mü?
 */
export function isIstanbulToday(date: Date | string): boolean {
  const d = typeof date === "string" ? new Date(date) : date;
  const istanbulDate = toZonedTime(d, ISTANBUL_TZ);
  const istanbulNow = getIstanbulDate();
  return isSameDay(istanbulDate, istanbulNow);
}

/**
 * Istanbul timezone'unda tarih aralığı oluştur (başlangıç ve bitiş)
 * @param {Date|string} startDate - Başlangıç tarihi
 * @param {Date|string} endDate - Bitiş tarihi
 * @returns {{start: Date, end: Date}} Istanbul timezone'unda tarih aralığı
 */
export function getIstanbulDateRange(
  startDate: Date | string,
  endDate: Date | string
): { start: Date; end: Date } {
  const start = getIstanbulDateStart(startDate);
  const end = getIstanbulDateEnd(endDate);

  // UTC'ye çevir (API için)
  return {
    start: fromZonedTime(start, ISTANBUL_TZ),
    end: fromZonedTime(end, ISTANBUL_TZ),
  };
}

/**
 * Istanbul timezone'unda şu anki zamanı ISO string olarak döndür
 * @returns {string} ISO string (UTC)
 */
export function getIstanbulNowISO(): string {
  return new Date().toISOString();
}

/**
 * Istanbul timezone'unda tarihi ISO string olarak döndür
 * @param {Date|string} date - Tarih
 * @returns {string} ISO string (UTC)
 */
export function toIstanbulISO(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toISOString();
}

/**
 * TR (Istanbul) datetime-local string'i UTC ISO'ya cevirir.
 * @param value - "yyyy-MM-ddTHH:mm" formatinda yerel TR saat
 */
export function trDateTimeLocalToUtcIso(
  value: string | null | undefined
): string | null {
  if (!value) return null;
  const utcDate = fromZonedTime(value, ISTANBUL_TZ);
  if (isNaN(utcDate.getTime())) return null;
  return utcDate.toISOString();
}

/**
 * UTC ISO tarihi TR (Istanbul) datetime-local formatina cevirir.
 * @param value - UTC ISO string
 */
export function utcIsoToTrDateTimeLocal(
  value: string | null | undefined
): string {
  if (!value) return "";
  const utcDate = new Date(value);
  if (isNaN(utcDate.getTime())) return "";
  return formatTz(toZonedTime(utcDate, ISTANBUL_TZ), "yyyy-MM-dd'T'HH:mm", {
    timeZone: ISTANBUL_TZ,
  });
}
